import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";

function parseMetadata(document: KnowledgeBaseItem): Record<string, unknown> {
  const metadata = document.metadata || {};
  if (typeof metadata === "string") {
    try {
      return JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return metadata as Record<string, unknown>;
}

/** Space IDs for a knowledge-base note (metadata.spaceIds, then legacy fields). */
export function getSpaceIdsFromKbItem(document: KnowledgeBaseItem): string[] {
  const parsedMetadata = parseMetadata(document);

  if (Array.isArray(parsedMetadata.spaceIds)) {
    return parsedMetadata.spaceIds.filter((id: unknown) => id != null).map(String);
  }
  if (parsedMetadata.spaceId) {
    return [String(parsedMetadata.spaceId)];
  }
  if (document.spaceId) {
    return [String(document.spaceId)];
  }
  return [];
}

/** All space IDs linked to a KB item (column, array, and metadata). */
export function collectSpaceIdsForCounts(item: KnowledgeBaseItem): string[] {
  const ids = new Set<string>();
  if (item.spaceId) ids.add(String(item.spaceId));
  (item.spaceIds ?? []).forEach((s) => ids.add(String(s)));
  const parsedMetadata = parseMetadata(item);
  if (parsedMetadata.spaceId) ids.add(String(parsedMetadata.spaceId));
  if (Array.isArray(parsedMetadata.spaceIds)) {
    (parsedMetadata.spaceIds as unknown[]).forEach((s) => ids.add(String(s)));
  }
  return [...ids];
}

/** Topic IDs for a knowledge-base note (metadata.topicIds, then legacy fields). */
export function getTopicIdsFromKbItem(document: KnowledgeBaseItem): string[] {
  const parsedMetadata = parseMetadata(document);

  if (Array.isArray(parsedMetadata.topicIds)) {
    return parsedMetadata.topicIds.filter((id: unknown) => id != null).map(String);
  }
  if (parsedMetadata.topicId) {
    return [String(parsedMetadata.topicId)];
  }
  if (document.topicId) {
    return [String(document.topicId)];
  }
  return [];
}
